import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, absoluteUrl, formatError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { TrendUp, TrendDown, Clock, Buildings, Receipt, PencilSimple, Trash, ImageSquare, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [workspace, setWorkspace] = useState({});
  const nav = useNavigate();

  const loadWorkspace = async () => {
    try { const { data } = await api.get("/workspace/settings"); setWorkspace(data); } catch {}
  };
  useEffect(() => { loadWorkspace(); }, []);

  useEffect(() => { api.get("/dashboard/company").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;

  const { financial, monthly } = data;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 fade-in" data-testid="company-dashboard">
      <HomeHero workspace={workspace} isAdmin={user?.role === "admin"} onSaved={loadWorkspace} />

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {workspace.name || "Gray Owl"}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900">{greet()}, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-neutral-500 mt-1">Your money in, money out, at a glance.</p>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Financial Overview</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total Income" value={fmt(financial.income)} icon={TrendUp} tone="green" testId="f-income" />
          <Stat label="Total Expense" value={fmt(financial.expense)} icon={TrendDown} tone="red" testId="f-expense" />
          <Stat label="Book Profit" value={fmt(financial.profit)} tone={financial.profit >= 0 ? "green" : "red"} testId="f-profit" />
          <Stat label="Profit After Tax" value={fmt(financial.profit_after_tax)} tone={(financial.profit_after_tax || 0) >= 0 ? "green" : "red"} testId="f-profit-after-tax" />
          <Stat label="Receivables" value={fmt(financial.receivable)} icon={Clock} tone="amber" testId="f-receivable" />
          <Stat label="Payables" value={fmt(financial.payable)} icon={Clock} tone="red" />
          <Stat label="Invoiced" value={fmt(financial.invoiced)} icon={Receipt} />
          <Stat label="Invoice Outstanding" value={fmt(financial.invoice_outstanding)} tone="amber" />
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3 flex items-center gap-2">
          Tax Position (TDS &amp; GST)
          <span className="text-[10px] normal-case tracking-normal text-neutral-400 font-normal">Auto-computed from every finance entry</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="TDS Receivable" value={fmt(financial.tds_receivable)} tone="blue" testId="f-tds-recv" />
          <Stat label="TDS Payable" value={fmt(financial.tds_payable)} tone="amber" testId="f-tds-pay" />
          <Stat label="GST Output (collected)" value={fmt(financial.gst_output)} tone="amber" testId="f-gst-out" />
          <Stat label="GST Input (claimable)" value={fmt(financial.gst_input)} tone="blue" testId="f-gst-in" />
          <Stat label="Net GST Liability" value={fmt(financial.net_gst_liability)} tone={(financial.net_gst_liability || 0) >= 0 ? "red" : "green"} testId="f-gst-net" />
          <Stat label="Net TDS Position" value={fmt(financial.net_tds_position)} tone={(financial.net_tds_position || 0) >= 0 ? "green" : "red"} testId="f-tds-net" />
        </div>
      </div>

      <div className="dos-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Monthly Cash Flow</div>
          <button data-testid="view-reports" onClick={() => nav("/reports")} className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors duration-200">Full reports <ArrowRight size={12} /></button>
        </div>
        <div className="space-y-2">
          {monthly.length === 0 && <div className="text-sm text-neutral-500">No data yet.</div>}
          {monthly.map((m) => {
            const max = Math.max(...monthly.map((x) => Math.max(x.income, x.expense)), 1);
            return (
              <div key={m.month} className="space-y-1">
                <div className="flex items-center justify-between text-[11px] text-neutral-500">
                  <span className="font-mono">{m.month}</span>
                  <span className={m.income - m.expense >= 0 ? "text-emerald-600" : "text-red-500"}>{fmt(m.income - m.expense)}</span>
                </div>
                <div className="flex gap-1 h-2">
                  <div className="bg-emerald-500 rounded-sm" style={{ width: `${(m.income / max) * 100}%` }} />
                  <div className="bg-red-400 rounded-sm" style={{ width: `${(m.expense / max) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HomeHero({ workspace, isAdmin, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef();

  const pick = () => fileRef.current?.click();

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/workspace/settings", { home_image_url: data.url });
      toast.success("Home image updated");
      onSaved && onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.patch("/workspace/settings", { home_image_url: "" });
      toast.success("Home image removed");
      onSaved && onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const hasImage = !!workspace.home_image_url;
  return (
    <div className="relative rounded-xl overflow-hidden border border-neutral-200 bg-neutral-900" data-testid="home-hero" style={{ aspectRatio: "16/5" }}>
      {hasImage ? (
        <img src={absoluteUrl(workspace.home_image_url)} alt="home hero" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-neutral-500 bg-gradient-to-br from-neutral-800 via-neutral-900 to-black">
          <div className="flex flex-col items-center gap-2 text-neutral-400">
            <ImageSquare size={40} weight="thin" />
            <div className="text-sm">No hero image set</div>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          {workspace.logo_url ? (
            <img src={absoluteUrl(workspace.logo_url)} alt="logo" data-testid="home-logo" className="w-14 h-14 rounded-lg object-contain bg-white/95 p-1 border border-white/40" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-white/95 flex items-center justify-center border border-white/40">
              <Buildings size={28} weight="fill" className="text-neutral-900" />
            </div>
          )}
          <div className="text-white">
            <div className="font-display text-2xl md:text-3xl font-bold leading-none">{workspace.name || "Gray Owl"}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/80 mt-1">{workspace.tagline || "Workspace"}</div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="home-hero-file" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <button data-testid="home-hero-upload" onClick={pick} disabled={uploading} className="px-3 py-1.5 rounded-md bg-white/95 hover:bg-white text-neutral-900 text-xs font-medium flex items-center gap-1.5 shadow-sm">
              <PencilSimple size={12} /> {uploading ? "Uploading…" : (hasImage ? "Change image" : "Upload image")}
            </button>
            {hasImage && (
              <button data-testid="home-hero-clear" onClick={clear} disabled={busy} className="px-3 py-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium flex items-center gap-1.5 shadow-sm">
                <Trash size={12} /> Remove
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "neutral", testId }) {
  const toneCls = { green: "text-emerald-600 bg-emerald-50", red: "text-red-600 bg-red-50", amber: "text-amber-600 bg-amber-50", blue: "text-blue-600 bg-blue-50", neutral: "text-neutral-600 bg-neutral-100" }[tone];
  return (
    <div className="dos-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-md flex items-center justify-center ${toneCls}`}><Icon size={14} weight="bold" /></div>}
      </div>
      <div className="font-display text-xl md:text-2xl font-bold text-neutral-900 mt-2">{value}</div>
    </div>
  );
}
